"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { rubberBand, Spring, SPRING } from "@/lib/glass/spring";

export interface GlassSegmentedItem<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional id for the item button (tabs: lets a panel point back with aria-labelledby). */
  id?: string;
  /** Tabs only: id of the panel this tab shows. */
  controls?: string;
}

export interface GlassSegmentedProps<T extends string> {
  items: ReadonlyArray<GlassSegmentedItem<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  size?: "sm" | "md" | "lg";
  /** "radio" (default) for a one-of-N filter, "tabs" when it switches panels. */
  semantics?: "radio" | "tabs";
  /** Stretch the items to fill the width (sign-in tabs). */
  fill?: boolean;
  className?: string;
  style?: CSSProperties;
}

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Segmented control with a liquid "droplet" selection.
 *
 * - Concentric radii: track radius R with padding p, selection radius R - p.
 * - Moving to another option, the leading edge travels first (spring
 *   response 0.28) and the trailing edge follows (0.42), so the selection
 *   stretches like a drop and settles with under 1% overshoot.
 * - Drag the selection: it turns into a clear droplet that magnifies the
 *   labels under it, rubber-bands at the ends and snaps to the nearest
 *   option on release.
 * - Keyboard: arrow keys, Home and End move and select; one tab stop.
 * - Semantics: radiogroup / radio (aria-checked), or tablist / tab
 *   (aria-selected) when it switches panels.
 * - On its own it is a clear glass track; inside another glass surface the
 *   track becomes a plain fill (see .g-seg in glass.css).
 */
export default function GlassSegmented<T extends string>({
  items,
  value,
  onChange,
  ariaLabel,
  size = "md",
  semantics = "radio",
  fill = false,
  className,
  style,
}: GlassSegmentedProps<T>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const springs = useRef<{ L: Spring; R: Spring } | null>(null);
  const placedIndex = useRef<number | null>(null);
  const dragging = useRef(false);

  const index = Math.max(0, items.findIndex((it) => it.value === value));
  const tabs = semantics === "tabs";

  const getSprings = useCallback(() => {
    if (!springs.current) {
      const L = new Spring(0, { ...SPRING.MORPH, epsilon: 0.05 });
      const R = new Spring(0, { ...SPRING.MORPH, epsilon: 0.05 });
      const render = () => {
        const t = thumbRef.current;
        if (!t) return;
        t.style.transform = `translateX(${L.x.toFixed(2)}px)`;
        t.style.width = `${Math.max(10, R.x - L.x).toFixed(2)}px`;
      };
      L.onUpdate = render;
      R.onUpdate = render;
      springs.current = { L, R };
    }
    return springs.current;
  }, []);

  const edges = useCallback((i: number): [number, number] | null => {
    const b = itemRefs.current[i];
    if (!b) return null;
    return [b.offsetLeft, b.offsetLeft + b.offsetWidth];
  }, []);

  /** Move the selection to option i (animated unless `jump`). */
  const place = useCallback(
    (i: number, jump: boolean) => {
      const e = edges(i);
      if (!e) return;
      const { L, R } = getSprings();
      const [l, r] = e;
      if (jump) {
        L.set(l);
        R.set(r);
      } else {
        const right = l > L.x;
        L.to(l, right ? SPRING.TRAIL.response : SPRING.LEAD.response);
        R.to(r, right ? SPRING.LEAD.response : SPRING.TRAIL.response);
      }
      placedIndex.current = i;
      // Keep the selected option visible when the track scrolls (phones).
      const rail = railRef.current;
      if (rail && rail.scrollWidth > rail.clientWidth + 1) {
        if (l < rail.scrollLeft) rail.scrollLeft = l - 8;
        else if (r > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = r - rail.clientWidth + 8;
      }
    },
    [edges, getSprings]
  );

  // Place on mount and animate whenever the selected value changes.
  useIsoLayoutEffect(() => {
    itemRefs.current.length = items.length;
    const first = placedIndex.current == null;
    place(index, first);
    if (first) rootRef.current?.setAttribute("data-ready", "");
  }, [index, items.length, place]);

  // Re-measure (without motion) when the layout or fonts change.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const refresh = () => {
      rail.toggleAttribute("data-scrolls", rail.scrollWidth > rail.clientWidth + 1);
      if (!dragging.current && placedIndex.current != null) place(placedIndex.current, true);
    };
    refresh();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(refresh) : null;
    ro?.observe(rail);
    itemRefs.current.forEach((b) => b && ro?.observe(b));
    let alive = true;
    document.fonts?.ready.then(() => {
      if (alive) refresh();
    });
    return () => {
      alive = false;
      ro?.disconnect();
    };
  }, [items.length, place]);

  useEffect(() => () => {
    springs.current?.L.stop();
    springs.current?.R.stop();
  }, []);

  const commit = useCallback(
    (i: number) => {
      const next = items[i];
      if (!next) return;
      if (next.value !== value) onChange(next.value);
      else place(i, false);
    },
    [items, value, onChange, place]
  );

  /* ---------- drag ---------- */

  useEffect(() => {
    const rail = railRef.current;
    const root = rootRef.current;
    if (!rail || !root) return;
    let drag: { id: number; x0: number; last: number; moved: boolean; half: number } | null = null;

    const magnify = (c: number) => {
      itemRefs.current.forEach((b) => {
        if (!b) return;
        const bc = b.offsetLeft + b.offsetWidth / 2;
        const k = Math.max(0, 1 - Math.abs(bc - c) / b.offsetWidth);
        b.style.scale = (1 + 0.14 * k).toFixed(3);
      });
    };
    const clearMagnify = () => itemRefs.current.forEach((b) => b && (b.style.scale = ""));
    const centerOf = (e: PointerEvent) => e.clientX - rail.getBoundingClientRect().left + rail.scrollLeft;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !e.isPrimary) return;
      // A track that scrolls sideways keeps touch for scrolling, not dragging.
      if (e.pointerType !== "mouse" && rail.hasAttribute("data-scrolls")) return;
      const cur = itemRefs.current[placedIndex.current ?? 0];
      drag = { id: e.pointerId, x0: e.clientX, last: e.clientX, moved: false, half: (cur?.offsetWidth ?? 40) / 2 };
    };
    const onMove = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return;
      if (!drag.moved) {
        if (Math.abs(e.clientX - drag.x0) < 4) return;
        drag.moved = true;
        dragging.current = true;
        root.setAttribute("data-dragging", "");
        try {
          rail.setPointerCapture(e.pointerId);
        } catch {
          /* capture is a nicety */
        }
      }
      const { L, R } = getSprings();
      const W = rail.scrollWidth;
      const c = centerOf(e);
      let l = c - drag.half;
      let r = c + drag.half;
      if (l < 0) {
        const nl = rubberBand(l, 0, Infinity, 16);
        r = nl + (r - l);
        l = nl;
      }
      if (r > W) {
        const nr = rubberBand(r, -Infinity, W, 16);
        l = nr - (r - l);
        r = nr;
      }
      const toRight = e.clientX >= drag.last;
      drag.last = e.clientX;
      L.to(l, toRight ? 0.2 : 0.12);
      R.to(r, toRight ? 0.12 : 0.2);
      magnify(c);
    };
    const end = (e: PointerEvent, cancel: boolean) => {
      if (!drag || e.pointerId !== drag.id) return;
      const d = drag;
      drag = null;
      if (!d.moved) return; // a plain tap: the item's own click handles it
      dragging.current = false;
      root.removeAttribute("data-dragging");
      clearMagnify();
      if (cancel) {
        place(placedIndex.current ?? 0, false);
        return;
      }
      const c = centerOf(e);
      let best = 0;
      let bestD = Infinity;
      itemRefs.current.forEach((b, j) => {
        if (!b) return;
        const dd = Math.abs(b.offsetLeft + b.offsetWidth / 2 - c);
        if (dd < bestD) {
          bestD = dd;
          best = j;
        }
      });
      commit(best);
    };
    const onUp = (e: PointerEvent) => end(e, false);
    const onCancel = (e: PointerEvent) => end(e, true);

    rail.addEventListener("pointerdown", onDown);
    rail.addEventListener("pointermove", onMove);
    rail.addEventListener("pointerup", onUp);
    rail.addEventListener("pointercancel", onCancel);
    return () => {
      rail.removeEventListener("pointerdown", onDown);
      rail.removeEventListener("pointermove", onMove);
      rail.removeEventListener("pointerup", onUp);
      rail.removeEventListener("pointercancel", onCancel);
    };
  }, [commit, getSprings, place]);

  /* ---------- keyboard ---------- */

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const n = items.length;
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (index + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (index - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    if (next < 0) return;
    e.preventDefault();
    commit(next);
    itemRefs.current[next]?.focus();
  };

  const cls = ["g-seg", "glass", size !== "md" && size, fill && "g-seg-stretch", className].filter(Boolean).join(" ");

  return (
    <div ref={rootRef} className={cls} style={fill ? { display: "flex", ...style } : style}>
      <div
        ref={railRef}
        className="g-seg-rail"
        role={tabs ? "tablist" : "radiogroup"}
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        style={fill ? { flex: 1 } : undefined}
      >
        <span ref={thumbRef} className="g-seg-thumb" aria-hidden />
        {items.map((it, i) => {
          const on = i === index;
          return (
            <button
              key={it.value}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              id={it.id}
              type="button"
              role={tabs ? "tab" : "radio"}
              aria-checked={tabs ? undefined : on}
              aria-selected={tabs ? on : undefined}
              aria-controls={tabs ? it.controls : undefined}
              tabIndex={on ? 0 : -1}
              className="g-seg-item"
              onClick={() => commit(i)}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
